import { useCallback, useRef, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';

import {
  pauseVaultSubscriptions,
  resumeVaultSubscriptions,
} from 'src/pages/admin/services/vaultService';

import { VAULTS_NS as NS } from './constants';
import { type OperationsControl, operationsErrorMessages, validatePauseReason } from './helpers';

export interface OperationsAlert {
  severity: 'error';
  messages: string[];
}

/**
 * XBIT-4242: the Operations block's dialog — pause (and take over a manager's pause) with a reason, or resume with a
 * confirmation. One request at a time; whatever the answer, the vault is read again (onSettled), so the block shows
 * what the engine now says. A replay (applied=false) is reported as such, not as a change.
 */
export const useVaultOperations = ({
  vaultId,
  onSettled,
}: {
  vaultId: string;
  onSettled: () => Promise<unknown> | void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [mode, setMode] = useState<OperationsControl | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [alert, setAlert] = useState<OperationsAlert | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // From the submit until the vault has been read again: the block's control is the old one until then.
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const open = useCallback((next: OperationsControl) => {
    setReason('');
    setError(undefined);
    setAlert(null);
    setMode(next);
  }, []);

  const close = useCallback(() => {
    if (!inFlight.current) setMode(null);
  }, []);

  const submit = useCallback(async () => {
    if (inFlight.current || !mode) return;
    if (mode !== 'resume') {
      const invalid = validatePauseReason(reason);
      setError(invalid);
      if (invalid) return;
    }
    inFlight.current = true;
    setSubmitting(true);
    setBusy(true);
    try {
      const change =
        mode === 'resume'
          ? await resumeVaultSubscriptions(vaultId)
          : await pauseVaultSubscriptions(vaultId, reason.trim());
      setMode(null);
      notify(translate(`${NS}.detail.operations.done.${change.applied ? mode : 'replay'}`), {
        type: change.applied ? 'success' : 'info',
      });
    } catch (err) {
      setAlert({ severity: 'error', messages: operationsErrorMessages(err, translate) });
    } finally {
      inFlight.current = false;
      setSubmitting(false);
      try {
        await onSettled();
      } finally {
        setBusy(false);
      }
    }
  }, [mode, notify, onSettled, reason, translate, vaultId]);

  return { mode, reason, setReason, error, alert, submitting, busy, open, close, submit };
};

export type VaultOperationsForm = ReturnType<typeof useVaultOperations>;
